import * as React from 'react'
import { Button } from '@repo/studio-design-system-ui-button'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from '@repo/react-router-locale/locale-router'
import { ROUTES } from '@repo/studio-utils/routes'
import { LFS_URL } from '@repo/studio-utils/config/env'
import { ArrowRightIcon } from '@repo/studio-design-system-ui-icons/arrows/arrow-right'
import { SlidingIcon } from '@repo/studio-components-sliding-icon'
import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import { TopBanner } from '../../top-banner'
import { CountdownTiles } from '../../countdown-tiles'
import { localStore } from 'utils/storage'
import {
  AI_VIDEO_PROMO_DISMISSED_KEY,
  AI_VIDEO_PROMO_END_DATE,
  isAiVideoPromoActive,
} from './promo-config'
import { useCountdown } from './use-countdown'

const COUNTDOWN_UNITS = ['days', 'hours', 'minutes', 'seconds'] as const

export function AiVideoPromoBanner() {
  const { t } = useTranslation('banners')
  const [dismissed, setDismissed] = React.useState(
    () => localStore.getString(AI_VIDEO_PROMO_DISMISSED_KEY) === '1',
  )
  const timeLeft = useCountdown(AI_VIDEO_PROMO_END_DATE, !dismissed)

  const handleDismiss = React.useCallback(() => {
    localStore.setString(AI_VIDEO_PROMO_DISMISSED_KEY, '1')
    setDismissed(true)
  }, [])

  // Self-gate so the strip disappears the moment the window closes — a safety
  // net in case the manual Monday removal slips (see promo-config.ts).
  if (timeLeft.isComplete || !isAiVideoPromoActive()) {
    return null
  }

  return (
    <TopBanner visible={!dismissed} variant="aurora">
      {/* Mirrors the dismiss button's width so the content stays centered. */}
      <span aria-hidden className="size-6 shrink-0" />
      <TopBanner.Center className="min-w-0 flex-row items-center justify-center gap-2 md:gap-3">
        <img
          src={`${LFS_URL}/studio/video/mascots/happyhorse/icon.webp`}
          alt=""
          className="hidden size-12 shrink-0 md:block"
        />
        <span className="typography-12 md:typography-14 text-balance text-center text-service-white-white">
          <Trans
            ns="banners"
            i18nKey="studio.banners.ai_video_promo.headline"
            components={{
              product: <span className="font-bold" />,
              highlight: <span className="text-studio-yellow-500 font-bold" />,
            }}
          />
        </span>
        <div className="hidden items-center gap-2 xl:flex">
          <span className="typography-12 text-service-white-white shrink-0">
            {t('studio.banners.ai_video_promo.countdown_prefix')}
          </span>
          <CountdownTiles
            values={timeLeft}
            units={COUNTDOWN_UNITS}
            i18nPrefix="studio.banners.ai_video_promo.countdown"
          />
        </div>
        {/* Coin-subscription pattern: roomy button on desktop, tiny on mobile. */}
        <Button asChild variant="overlay" size="sm" className="hidden shrink-0 md:flex">
          <Link to={ROUTES.AI_VIDEO_GENERATOR.ROOT}>
            {t('studio.banners.ai_video_promo.cta')}
            <SlidingIcon>
              <ArrowRightIcon />
            </SlidingIcon>
          </Link>
        </Button>
        <Button asChild variant="overlay" size="xs" className="flex shrink-0 md:hidden">
          <Link to={ROUTES.AI_VIDEO_GENERATOR.ROOT}>
            {t('studio.banners.ai_video_promo.cta')}
            <SlidingIcon>
              <ArrowRightIcon />
            </SlidingIcon>
          </Link>
        </Button>
      </TopBanner.Center>
      <Button
        iconOnly
        variant="ghost"
        size="xs"
        onClick={handleDismiss}
        aria-label={t('studio.banners.ai_video_promo.dismiss_aria_label')}
        className="text-service-white-white shrink-0"
      >
        <XCloseIcon />
      </Button>
    </TopBanner>
  )
}
